import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from '../utils/axios';
import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
  DEFAULT_LOOKUP_PAGE_SIZE,
} from '../constants';
import type {
  BrandRecord,
  BrandTotalsParams,
  PagedResult,
} from '../types/index';

type UseBrandsParams = BrandTotalsParams & {
  enabled?: boolean;
  loadAll?: boolean;
};

type UseBrandsResult = {
  data: BrandRecord[];
  error: unknown;
  isLoading: boolean;
  refetch: () => Promise<void>;
  total: number;
};

type BrandsState = Omit<UseBrandsResult, 'refetch'> & {
  requestKey: string;
};

function normalizeBrands(brands: BrandRecord[] = []): BrandRecord[] {
  return (brands || []).map((brand) => ({
    ...brand,
    id: brand?.id ?? brand?.brandId,
    name: brand?.name || '',
  }));
}

export async function getBrandTotals({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LIST_PAGE_SIZE,
}: BrandTotalsParams = {}): Promise<PagedResult<BrandRecord>> {
  const response = await axios.get('/brand/totals', {
    params: { organizationId, page, pageSize },
  });

  return {
    page: Number(response.data?.page ?? page),
    pageSize: Number(response.data?.pageSize ?? pageSize),
    total: Number(response.data?.total ?? 0),
    data: normalizeBrands(Array.isArray(response.data?.data) ? response.data.data : []),
  };
}

export async function getBrands(
  organizationId: number | string,
  page = DEFAULT_LIST_PAGE,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE
): Promise<BrandRecord[]> {
  const response = await axios.get('/brand', {
    params: { organizationId, page, pageSize },
  });
  return normalizeBrands(Array.isArray(response.data) ? response.data : []);
}

export async function getAllBrands(
  organizationId: number | string,
  pageSize = DEFAULT_LOOKUP_PAGE_SIZE
): Promise<BrandRecord[]> {
  const allBrands: BrandRecord[] = [];
  let page = DEFAULT_LIST_PAGE;

  while (true) {
    const result = await getBrandTotals({ organizationId, page, pageSize });
    allBrands.push(...result.data);

    const resultPageSize = Number(result.pageSize);
    const resultTotal = Number(result.total);
    const hasValidPagination = Number.isFinite(resultPageSize)
      && resultPageSize > 0
      && Number.isFinite(resultTotal)
      && resultTotal >= 0;

    if (!result.data.length || !hasValidPagination || (page + 1) * resultPageSize >= resultTotal) {
      return allBrands;
    }

    page += 1;
  }
}

export async function getBrandById(
  organizationId: number | string,
  brandId: number | string
): Promise<BrandRecord | null> {
  const response = await axios.get('/brand/byid', {
    params: { organizationId, brandId },
  });
  return response.data || null;
}

export async function addBrand(payload: unknown): Promise<unknown> {
  const response = await axios.post('/brand/add', payload);
  return response.data;
}

export async function editBrand(payload: unknown): Promise<unknown> {
  const response = await axios.post('/brand/edit', payload);
  return response.data;
}

export default function useBrands({
  organizationId,
  page = DEFAULT_LIST_PAGE,
  enabled = true,
  loadAll = false,
  pageSize = loadAll ? DEFAULT_LOOKUP_PAGE_SIZE : DEFAULT_LIST_PAGE_SIZE,
}: UseBrandsParams = {}): UseBrandsResult {
  const requestIdRef = useRef(0);
  const requestKey = useMemo(
    () => `${String(organizationId || '')}:${page}:${pageSize}:${loadAll}`,
    [loadAll, organizationId, page, pageSize],
  );
  const [state, setState] = useState<BrandsState>({
    data: [],
    total: 0,
    error: null,
    isLoading: enabled,
    requestKey: '',
  });

  const refetch = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!enabled || !organizationId) {
      setState({ data: [], total: 0, error: null, isLoading: false, requestKey });
      return;
    }

    setState((current) => ({
      data: current.requestKey === requestKey ? current.data : [],
      total: current.requestKey === requestKey ? current.total : 0,
      error: null,
      isLoading: true,
      requestKey,
    }));

    try {
      if (loadAll) {
        const results = await getAllBrands(organizationId, pageSize);
        if (requestId !== requestIdRef.current) return;
        setState({
          data: results,
          total: results.length,
          error: null,
          isLoading: false,
          requestKey,
        });
      } else {
        const results = await getBrandTotals({ organizationId, page, pageSize });
        if (requestId !== requestIdRef.current) return;
        setState({
          data: results.data,
          total: results.total,
          error: null,
          isLoading: false,
          requestKey,
        });
      }
    } catch (nextError) {
      if (requestId !== requestIdRef.current) return;
      setState((current) => ({
        ...current,
        error: nextError,
        isLoading: false,
        requestKey,
      }));
    }
  }, [enabled, loadAll, organizationId, page, pageSize, requestKey]);

  useEffect(() => {
    refetch().catch(() => null);
    return () => {
      requestIdRef.current += 1;
    };
  }, [refetch]);

  const shouldLoad = Boolean(enabled && organizationId);
  const isCurrentRequest = shouldLoad && state.requestKey === requestKey;

  return {
    data: isCurrentRequest ? state.data : [],
    error: isCurrentRequest ? state.error : null,
    isLoading: shouldLoad && (!isCurrentRequest || state.isLoading),
    refetch,
    total: isCurrentRequest ? state.total : 0,
  };
}
